/**
 * Tenant configuration utility functions.
 * 
 * These functions provide access to tenant-specific configuration
 * from the backend rather than environment variables.
 * This ensures proper multi-tenant isolation.
 */

import { useTenantStore } from '@/lib/stores/tenant-store'

export interface TenantConfig {
    id: string
    name: string
    subdomain: string
    baseDomain: string
    fullDomain: string
    redmineBaseUrl: string
    backendUrl: string
    isActive: boolean
    // SECURITY: Database fields removed - all DB operations proxied through backend
}

/**
 * Get tenant configuration from cookie (set by middleware)
 * This is the primary way to get tenant config in client components.
 */
export function getTenantFromCookie(): TenantConfig | null {
    if (typeof window === 'undefined') return null

    try {
        const cookies = document.cookie.split(';')
        const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))

        if (!tenantCookie) return null

        const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
        const tenantData = JSON.parse(decodeURIComponent(cookieValue))

        return {
            id: tenantData.id,
            name: tenantData.name,
            subdomain: tenantData.subdomain,
            baseDomain: tenantData.baseDomain || 'oxigen.local',
            fullDomain: tenantData.fullDomain || `${tenantData.subdomain}.${tenantData.baseDomain}`,
            redmineBaseUrl: tenantData.redmineBaseUrl,
            backendUrl: tenantData.backendUrl,
            isActive: tenantData.isActive
        }
    } catch (error) {
        console.error('Failed to parse tenant cookie:', error)
        return null
    }
}

/**
 * Get Redmine base URL for the current tenant.
 * Falls back to a default if no tenant context is available.
 */
export function getRedmineBaseUrl(): string {
    const tenant = getTenantFromCookie()

    if (tenant?.redmineBaseUrl) {
        return tenant.redmineBaseUrl
    }

    // Fallback for development/testing (single-tenant mode)
    return 'https://oxigen.oxinus.holdings'
}

/**
 * Get backend URL for API calls from tenant configuration.
 * This retrieves the dynamic backend URL for the current tenant.
 * Returns null if tenant context is not available.
 */
export function getBackendUrl(): string | null {
    const tenant = getTenantFromCookie()

    if (tenant && 'backendUrl' in tenant && tenant.backendUrl) {
        return tenant.backendUrl as string
    }

    // No tenant context available - caller must handle this
    console.warn('Backend URL not available: tenant context missing or backendUrl not configured')
    return null
}

/**
 * Get base domain for multi-tenancy from tenant config.
 * Falls back to development default if not available.
 */
export function getBaseDomain(): string {
    const tenant = getTenantFromCookie()
    if (tenant?.baseDomain) {
        return tenant.baseDomain
    }
    // Development default
    return 'oxigen.local'
}

/**
 * Check if we're in multi-tenant mode (subdomain present)
 */
export function isMultiTenantMode(): boolean {
    return getTenantFromCookie() !== null
}

/**
 * Get tenant configuration from Zustand store (for use in React components)
 * This is updated by the tenant store's fetchTenantFromCookie method.
 */
export function useTenantConfig() {
    const { currentTenant } = useTenantStore()
    return currentTenant
}
